import { useMemo, useState } from 'react';
import { terrainTileUrl, type TerrainMapView } from '../api/missions';
import { tileSquare } from './mapView';

/**
 * The ground under the map, as tiles.
 *
 * Every picture the platform had of a terrain was one flat square, and a flat
 * square stretched over a map that zooms is a blur: at full zoom on Altis one
 * pixel of a 2048 px render covers fifteen metres. The PLANOPS Atlas — the same
 * catalogue the panel takes its report maps from — publishes each terrain as a
 * tile pyramid, so the map asks for the zoom level it is actually drawing at
 * and every level is sharp.
 *
 * The tiles are laid inside the map's own square: at zoom `z` the world is a
 * grid of 2^z by 2^z of them, so one tile is `size / 2^z` canvas pixels
 * whatever the terrain measures in metres. Only the tiles the viewport can see
 * are rendered — a zoom of eight over a 64-tile grid is four thousand tiles,
 * and twenty of them are on screen.
 *
 * Two grids are drawn, not one. The detailed one is the level the screen is
 * worth; under it sits a coarse one, four times cheaper on each side, covering
 * the same ground. It is what makes the map feel immediate: the coarse level of
 * a terrain is a handful of tiles that the browser has usually already got, so
 * a pan or a zoom shows the ground straight away and sharpens a moment later,
 * instead of showing the bare background until the new level arrives. It also
 * covers the holes — a tile the Atlas does not have at the deep level, and every
 * tile of the seconds before a terrain's layer has finished downloading onto the
 * platform's own disk.
 */
export function TerrainTiles({
  world,
  map,
  size,
  worldSize,
  view,
}: {
  world: string;
  map: TerrainMapView;
  size: number;
  /** The terrain's own edge in metres, as the mission's definition records it. */
  worldSize: number;
  view: { scale: number; x: number; y: number };
}) {
  const [broken, setBroken] = useState<Set<string>>(new Set());

  // The Atlas measures the terrain too, and the two numbers can disagree - a
  // probe that read the wrong field, a terrain whose playable area is smaller
  // than its map. Both squares start at the world's own origin, the south-west
  // corner, which is the bottom-left of the canvas: the tile square is anchored
  // there and takes whatever room its own metres ask for, which is the pyramid's
  // width and not the terrain's (tileSquare).
  const { span, top } = tileSquare(map, worldSize, size);

  const { zoom, base, tiles, baseTiles } = useMemo(() => {
    // The zoom whose tiles are at least as dense as the screen: the map square
    // is size * scale pixels on screen, and the pyramid's level z is
    // tileSize * 2^z across.
    const wanted = Math.log2((span * view.scale) / map.tileSize);
    const z = Math.max(map.minZoom, Math.min(map.maxZoom, Math.ceil(wanted)));
    // Two levels below is sixteen times fewer tiles for the same ground, which
    // is a screenful in three or four requests instead of thirty.
    const zBase = Math.max(map.minZoom, z - 2);

    // What the viewport shows, in one grid's own pixels. The grid's origin is
    // `top` below the canvas's, which the y range has to account for.
    const gridOf = (level: number) => {
      const count = 2 ** level;
      const tilePx = span / count;
      const from = (offset: number) => -offset / view.scale / tilePx;
      const to = (offset: number) => (size - offset) / view.scale / tilePx;
      const first = (v: number) => Math.max(0, Math.floor(v) - 1);
      const last = (v: number) => Math.min(count - 1, Math.ceil(v) + 1);
      const offsetY = view.y + top * view.scale;
      const out: { key: string; x: number; y: number; left: number; top: number }[] = [];
      for (let x = first(from(view.x)); x <= last(to(view.x)); x++) {
        for (let y = first(from(offsetY)); y <= last(to(offsetY)); y++) {
          out.push({ key: `${level}/${x}/${y}`, x, y, left: x * tilePx, top: y * tilePx });
        }
      }
      return out;
    };

    return { zoom: z, base: zBase, tiles: gridOf(z), baseTiles: zBase === z ? [] : gridOf(zBase) };
  }, [map.tileSize, map.minZoom, map.maxZoom, size, span, top, view.scale, view.x, view.y]);

  const grid = (level: number, list: typeof tiles) => {
    const tilePx = span / 2 ** level;
    return list.map((tile) =>
      broken.has(tile.key) ? null : (
        <img
          key={tile.key}
          src={terrainTileUrl(world, map.layer, level, tile.x, tile.y)}
          alt=""
          draggable={false}
          loading="eager"
          decoding="async"
          onError={() => setBroken((was) => new Set(was).add(tile.key))}
          style={{
            position: 'absolute',
            left: tile.left,
            top: tile.top,
            // Half a pixel over, so neighbouring tiles do not show a seam of
            // background between them when the scale is not a round number.
            width: tilePx + 0.5,
            height: tilePx + 0.5,
          }}
        />
      ),
    );
  };

  return (
    <div
      className="map-tiles"
      aria-hidden="true"
      style={{ top, left: 0, width: span, height: span }}
    >
      {grid(base, baseTiles)}
      {grid(zoom, tiles)}
    </div>
  );
}
